import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import DrawerFilter from "../../../components/listing/DrawerFilter";
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";
import EessAutocomplete from "../../../components/autocomplete/EessAutocomplete";

// --- HOOK UTILITARIO ---
function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function FiltroInformacion({
  initialFilters = {
    fecha: "",
    informacion: "",
    cliente: "",
    eess: "",
  },
  onSubmit = () => {},
  onClose = () => {},
}) {
  // ===== ESTADOS DE FILTROS =====
  const [fecha, setFecha] = useState(initialFilters.fecha || "");
  const [informacion, setInformacion] = useState(initialFilters.informacion || "");
  const [clienteObj, setClienteObj] = useState(initialFilters.clienteObj || null);
  const [eessObj, setEessObj] = useState(initialFilters.eessObj || null);

  const debInformacion = useDebounced(informacion, 300);

  // ===== ESTADO UI =====
  const [isLoading, setIsLoading] = useState(false);

  // ===== AUTOCOMPLETE ESTADOS =====
  const [infoItems, setInfoItems] = useState([]);
  const [infoPage, setInfoPage] = useState(0);
  const [infoTotalPages, setInfoTotalPages] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const infoBoxRef = useRef(null);

  // ===== Cerrar autocomplete al click afuera =====
  useEffect(() => {
    const onDoc = (e) => {
      if (infoBoxRef.current && !infoBoxRef.current.contains(e.target)) {
        setInfoOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ===== LOGICA FETCH AUTOCOMPLETE =====
  useEffect(() => {
    if (!infoOpen) return;

    const loadInformacion = async () => {
      setInfoLoading(true);
      try {
        const data = await apiFetchJson(
          apiRoutes.informacion.listUrl({
            page: 0,
            size: 20,
            q: debInformacion,
          }),
        );

        setInfoItems(data?.items ?? []);
        setInfoPage(data?.page ?? 0);
        setInfoTotalPages(data?.totalPages ?? 0);
      } catch (e) {
        console.error("Autocomplete información:", e);
      } finally {
        setInfoLoading(false);
      }
    };

    loadInformacion();
  }, [debInformacion, infoOpen]);

  // ===== Scroll infinito =====
  const onInfoScroll = (e) => {
    const el = e.currentTarget;

    if (infoLoading) return;
    if (infoPage + 1 >= infoTotalPages) return;

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const nextPage = infoPage + 1;
      setInfoLoading(true);

      apiFetchJson(
        apiRoutes.informacion.listUrl({
          page: nextPage,
          size: 20,
          q: debInformacion,
        }),
      )
        .then((data) => {
          setInfoItems((prev) => [...prev, ...(data?.items ?? [])]);
          setInfoPage(data?.page ?? nextPage);
          setInfoTotalPages(data?.totalPages ?? infoTotalPages);
        })
        .catch((e) => console.error("Scroll información:", e))
        .finally(() => setInfoLoading(false));
    }
  };

  // ===== SUBMIT HANDLER =====
  const handleApply = () => {
    setIsLoading(true);
    
    onSubmit({
  fecha: fecha.trim(),
  informacion: informacion.trim(),
  clienteObj,
  eessObj,
});
    
    setIsLoading(false);
    onClose();
  };

  return (
    <DrawerFilter
      title="Filtros"
      onClose={onClose}
      onApply={handleApply}
      isLoading={isLoading}
    >
      {/* 1. FECHA */}
      <div>
        <label className="form-label-fil">Fecha:</label>
        <input
          type="date"
          className="input mt-1 w-full"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
        />
      </div>

      {/* 2. INFORMACION (Autocomplete Manual) */}
      <div ref={infoBoxRef} className="relative">
        <label className="form-label-fil">Información:</label>
        <input
          type="text"
          className="input mt-1 w-full"
          placeholder="Buscar información…"
          value={informacion}
          onChange={(e) => {
            setInformacion(e.target.value);
            setInfoOpen(true);
          }}
          onFocus={() => setInfoOpen(true)}
        />

        {infoOpen && (
          <div
            className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-56 overflow-auto text-sm"
            onScroll={onInfoScroll}
          >
            {infoItems.map((it) => (
              <button
                key={it.id}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0"
                onClick={() => {
                  setInformacion(it.descripcion);
                  setInfoOpen(false);
                }}
              >
                {it.descripcion}
              </button>
            ))}

            {infoLoading && (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                Cargando…
              </div>
            )}

            {!infoLoading && infoItems.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                Sin resultados
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3. CLIENTE */}
      <div>
  <label className="form-label-fil">Cliente:</label>
  <div className="mt-1 w-full">
    <ClienteAutocomplete
      value={clienteObj}
      onChange={setClienteObj}
      placeholder="Buscar cliente..."
    />
  </div>
</div>

      {/* 4. EE.SS. */}
      <div>
  <label className="form-label-fil">EE.SS.:</label>
  <div className="mt-1 w-full">
    <EessAutocomplete
      value={eessObj}
      onChange={setEessObj}
      placeholder="Buscar estación..."
    />
  </div>
</div>

    </DrawerFilter>
  );
}